import { generatePath } from 'react-router-dom'
import { ViewType } from '../assets/constants'
import ROUTES from '../assets/routes'
import { Exchange, exchangeFromJSON, exchangeToJSON } from '../proto/generated/common'
import {
    Security,
    SecurityCategory_CategoryCode,
    Segment,
} from '../proto/generated/security'
import { Snapshot } from '../reducer/security.reducer'
import { fetchSecurities, lookupSymbols } from '../services/apis/securities'
import { parseDDMMMyyDate } from './dateTime'
import { shortMonthNames } from './general'

/** Internal use only - reads from cache synchronously. Use getSecurityAsync for async fetch. */
export const getSecurityFromCache = (key: string): Security | undefined => {
    return window.securityManager.getSecurity(key)
}

export type PrimarySecurity = {
    key: string
    displayName?: string
    primary?: Security
    expressionMode: boolean
    expressionSecurities: Security[]
    underlying?: Security
    underlyingSecurityCode: string
}

/** Security currently traded on chart (single symbol or first expression leg). */
export const getChartTradedSecurity = (ps: PrimarySecurity): Security | undefined => {
    if (ps.primary?.code) return ps.primary
    if (ps.expressionSecurities?.length) return ps.expressionSecurities[0]
    return undefined
}

const hasQuantityMetadata = (sec?: Security) =>
    !!sec?.code && sec.lotSize != null && sec.categoryCode != null

/** Sync from store, else fetch by chart key (pick-from-chart / expression legs). */
export const resolveChartTradedSecurity = async (ps: PrimarySecurity): Promise<Security | undefined> => {
    const sync = getChartTradedSecurity(ps)
    if (hasQuantityMetadata(sync)) return sync
    const baseKey = ps.key ? getBaseSecurityKeyFromComparableToken(ps.key) : ''
    if (!baseKey) return sync
    return (await getSecurityAsync(baseKey)) ?? sync
}

/** Resolves primary security by fetching all needed securities and building the resolved object. */
export const resolvePrimarySecurity = async (payload: {
    key: string
    displayName?: string
    expressionMode: boolean
    securities: string[]
    underlyingSecurityCode: string
}): Promise<PrimarySecurity> => {
    const { key, displayName, expressionMode, securities: stateSecurities, underlyingSecurityCode } = payload
    const keysToFetch: string[] = [key]
    if (expressionMode && stateSecurities?.length) {
        keysToFetch.push(...stateSecurities)
        if (!underlyingSecurityCode && stateSecurities[0]) {
            const firstSec = await getSecurityAsync(stateSecurities[0])
            if (firstSec) {
                const underlyingCode = firstSec.hasFo ? firstSec.code : firstSec.underlyingSecurityCode
                if (underlyingCode) keysToFetch.push(getSecurityKey(firstSec.exchange, underlyingCode))
            }
        }
    }
    if (underlyingSecurityCode) keysToFetch.push(underlyingSecurityCode)
    await Promise.all([...new Set(keysToFetch)].filter(Boolean).map((k) => getSecurityAsync(k)))

    const primary = expressionMode ? null : getSecurityFromCache(key)
    const expressionSecurities = expressionMode ? stateSecurities.map((t) => getSecurityFromCache(t)).filter((v): v is Security => !!v) : []
    const underlyingKey = underlyingSecurityCode ?? (primary ? getSecurityKey(primary.exchange, primary.underlyingSecurityCode ?? '') : '')
    let underlying: Security | undefined = underlyingKey ? getSecurityFromCache(underlyingKey) : undefined
    if (expressionMode && !underlying && expressionSecurities[0]) {
        const firstSecurityUnderlyingCode = expressionSecurities[0].hasFo ? expressionSecurities[0].code : expressionSecurities[0].underlyingSecurityCode
        if (firstSecurityUnderlyingCode && expressionSecurities.every((s) => (s.hasFo && s.code === firstSecurityUnderlyingCode) || (!s.hasFo && s.underlyingSecurityCode === firstSecurityUnderlyingCode))) {
            underlying = getSecurityFromCache(getSecurityKey(expressionSecurities[0].exchange, firstSecurityUnderlyingCode))
        }
    }
    return {
        key,
        primary: primary ?? undefined,
        displayName,
        expressionMode,
        expressionSecurities,
        underlying,
        underlyingSecurityCode: underlyingSecurityCode ?? (primary ? getSecurityKey(primary.exchange, primary.underlyingSecurityCode ?? '') : '')
    }
}

export const getSecurityKey = (exchange: Exchange, code: string) => `${exchangeToJSON(exchange)}:${code}`

export const getWebsiteSecurityKey = (security: Security): string => {
    const exchange = exchangeToJSON(security.exchange)
    const urlIdentifier = security.urlIdentifier?.trim()
    if (urlIdentifier) {
        return `${exchange}:${urlIdentifier}`
    }
    return getSecurityKey(security.exchange, security.code)
}

/** Suffix on seriesGroup.securityList[0] so duplicate compare symbols get distinct series groups; strip before data/cache keys. */
export const COMPARABLE_INSTANCE_DELIM = '||cmp||'

export const getComparableSeriesGroupToken = (baseSecurityKey: string, seriesGroupId: string) =>
    `${baseSecurityKey}${COMPARABLE_INSTANCE_DELIM}${seriesGroupId}`

export const getBaseSecurityKeyFromComparableToken = (token: string): string => {
    const i = token.indexOf(COMPARABLE_INSTANCE_DELIM)
    return i === -1 ? token : token.slice(0, i)
}

export const getSecuritySymbol = (exchange: Exchange, symbol: string) => `${exchangeToJSON(exchange)}:${symbol}`

export const getExpressionSecurityKey = (exchange: Exchange, code: string) => `${exchangeToJSON(exchange)}__${code}`

export const getExchangeAndCodeFromKey = (key: string): [Exchange, string] => {
    const base = getBaseSecurityKeyFromComparableToken(key)
    let [exchange, code] = base.split(':')
    return [exchangeFromJSON(exchange) as Exchange, code]
}

const SECURITY_KEY_REGEX = /^[a-zA-Z]+:[a-zA-Z0-9_]+$/
const inflightSecurityFetches = new Map<string, Promise<Security | undefined>>()
const pendingSecurityKeys = new Set<string>()
const securityKeyWaiters = new Map<string, Array<(security: Security | undefined) => void>>()
let securityBatchScheduled = false

async function fetchSecurityByKey(key: string): Promise<Security | undefined> {
    if (!SECURITY_KEY_REGEX.test(key)) return undefined

    const [exchange, code] = getExchangeAndCodeFromKey(key)
    const { isNativeEmbedded, getSecurityFromNative } = await import('./nativeBridge')
    if (isNativeEmbedded()) {
        const securityString = await getSecurityFromNative(exchange.toString(), code)
        if (securityString) {
            const binaryString = atob(securityString)
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i)
            }
            const { Security } = await import('../proto/generated/security')
            const security = Security.decode(bytes)
            if (security?.code) {
                window.securityManager.setSecurity(security)
                return security
            }
        }
        return undefined
    }

    const securities = await fetchSecurities([{ exchange, code }])
    if (securities.length) {
        securities.forEach((sec) => window.securityManager.setSecurity(sec))
        return securities[0]
    }
    return undefined
}

function resolveSecurityKeyWaiters(key: string, security: Security | undefined) {
    const waiters = securityKeyWaiters.get(key)
    if (!waiters) return
    waiters.forEach((resolve) => resolve(security))
    securityKeyWaiters.delete(key)
}

async function flushSecurityFetchBatch() {
    securityBatchScheduled = false
    const keys = [...pendingSecurityKeys]
    pendingSecurityKeys.clear()

    const missingKeys: string[] = []
    for (const key of keys) {
        const cached = getSecurityFromCache(key)
        if (cached) {
            resolveSecurityKeyWaiters(key, cached)
        } else if (SECURITY_KEY_REGEX.test(key)) {
            missingKeys.push(key)
        } else {
            resolveSecurityKeyWaiters(key, undefined)
        }
    }

    if (!missingKeys.length) return

    const { isNativeEmbedded } = await import('./nativeBridge')
    if (isNativeEmbedded()) {
        for (const key of missingKeys) {
            const security = await fetchSecurityByKey(key)
            resolveSecurityKeyWaiters(key, security)
        }
        return
    }

    const toFetch = missingKeys.map((key) => {
        const [exchange, code] = getExchangeAndCodeFromKey(key)
        return { exchange, code }
    })

    try {
        const securities = await fetchSecurities(toFetch)
        securities.forEach((sec) => window.securityManager.setSecurity(sec))
    } catch {
        // waiters still resolved below from cache (undefined if fetch failed)
    }

    for (const key of missingKeys) {
        resolveSecurityKeyWaiters(key, getSecurityFromCache(key))
    }
}

function scheduleSecurityFetchBatch() {
    if (securityBatchScheduled) return
    securityBatchScheduled = true
    queueMicrotask(() => {
        void flushSecurityFetchBatch()
    })
}

export const getSecurityAsync = (key: string): Promise<Security | undefined> => {
    const cached = getSecurityFromCache(key)
    if (cached) return Promise.resolve(cached)

    const inflight = inflightSecurityFetches.get(key)
    if (inflight) return inflight

    const promise = new Promise<Security | undefined>((resolve) => {
        const waiters = securityKeyWaiters.get(key) ?? []
        waiters.push(resolve)
        securityKeyWaiters.set(key, waiters)
        pendingSecurityKeys.add(key)
        scheduleSecurityFetchBatch()
    })

    inflightSecurityFetches.set(key, promise)
    promise.finally(() => inflightSecurityFetches.delete(key))
    return promise
}

/** Resolve security keys in parallel; batches missing keys into a single API request per tick. */
export const resolveSecuritiesFromKeys = async (
    keys: string[],
    expressionMode = false
): Promise<{ securities: Security[]; map: Map<string, Security> }> => {
    const results = await Promise.all(keys.map((key) => getSecurityAsync(key)))
    const securities: Security[] = []
    const map = new Map<string, Security>()

    for (let i = 0; i < keys.length; i++) {
        const sec = results[i]
        if (!sec) continue
        securities.push(sec)
        map.set(getSecurityKey(sec.exchange, sec.code), sec)
        if (sec.underlyingSecurityCode && expressionMode && i === 0) {
            const underlyingKey = getSecurityKey(sec.exchange, sec.underlyingSecurityCode)
            if (!map.has(underlyingKey)) {
                const underlying = await getSecurityAsync(underlyingKey)
                if (underlying) map.set(underlyingKey, underlying)
            }
        }
    }

    return { securities, map }
}

export const getExchangeAndSymbolFromKey = (key: string): [Exchange, string] => {
    let [exchange, symbol] = key.includes(':') ? key.split(':') : [Exchange.NSE, key]
    let finalExchange = exchangeFromJSON(exchange)
    if([Exchange.EXCHANGE_UNKNOWN, Exchange.UNRECOGNIZED].includes(finalExchange)) {
        finalExchange = Exchange.NSE
    }
    return [finalExchange, symbol]
}


export const isValidExchange = (exchange: Exchange | undefined): boolean => {
    return (exchange !== undefined && exchange !== Exchange.EXCHANGE_UNKNOWN && exchange !== Exchange.UNRECOGNIZED)
}

const SHAREHOLDER_LOOKUP_EXCHANGES = [Exchange.NSE, Exchange.BSE]

/** Shareholder search rows may use DB symbol keys (M_M); security master uses trading codes (M&M). */
const getShareholderSearchSymbolVariants = (symbol: string): string[] => {
    const trimmed = symbol.trim()
    if (!trimmed) {
        return []
    }
    if (trimmed.includes('_')) {
        return [trimmed, trimmed.replace(/_/g, '&')]
    }
    return [trimmed]
}

export const normalizeExchangeValue = (exchange: Exchange | string | number | undefined): Exchange | undefined => {
    if (exchange === undefined || exchange === null) {
        return undefined
    }
    const parsed =
        typeof exchange === 'string'
            ? exchangeFromJSON(exchange)
            : typeof exchange === 'number'
              ? exchange
              : exchange
    return isValidExchange(parsed) ? parsed : undefined
}

const symbolFieldsMatch = (security: Security, normalizedSymbol: string): boolean => {
    const fields = [security.code, security.symbol, security.exchangeSymbol, security.urlIdentifier].filter(
        Boolean,
    ) as string[]
    return fields.some((field) => field.toUpperCase() === normalizedSymbol)
}

export const findCashSecurityBySymbol = (symbol: string): Security | undefined => {
    const normalized = symbol.trim().toUpperCase()
    if (!normalized) {
        return undefined
    }

    const matches = window.securityManager.getAllSecurities().filter((security) => {
        if (!isValidExchange(security.exchange)) {
            return false
        }
        if (security.segment !== undefined && security.segment !== Segment.CM) {
            return false
        }
        if (
            security.categoryCode &&
            security.categoryCode !== SecurityCategory_CategoryCode.STOCKS &&
            security.categoryCode !== SecurityCategory_CategoryCode.CATEGORY_CODE_UNKNOWN
        ) {
            return false
        }
        return symbolFieldsMatch(security, normalized)
    })

    if (!matches.length) {
        return undefined
    }
    if (matches.length === 1) {
        return matches[0]
    }

    const nseMatch = matches.find((security) => security.exchange === Exchange.NSE)
    const bseMatch = matches.find((security) => security.exchange === Exchange.BSE)
    if (nseMatch && bseMatch) {
        return nseMatch
    }

    const exactCode = matches.find((security) => security.code.toUpperCase() === normalized)
    if (exactCode) {
        return exactCode
    }

    return matches[0]
}

const lookupSecuritiesBySymbols = async (symbols: string[], exchange: Exchange): Promise<Security[]> => {
    if (!isValidExchange(exchange)) {
        return []
    }
    const trimmed = [...new Set(symbols.map((symbol) => symbol.trim()).filter(Boolean))]
    if (!trimmed.length) {
        return []
    }

    const response = await lookupSymbols(trimmed.map((symbol) => ({ exchange, symbol })))
    const securities = response.securities ?? []
    securities.forEach((security) => window.securityManager.setSecurity(security))
    return securities
}

const matchLookupResultToSymbol = (securities: Security[], requestedSymbol: string): Security | undefined => {
    for (const variant of getShareholderSearchSymbolVariants(requestedSymbol)) {
        const normalized = variant.trim().toUpperCase()
        const match = securities.find((security) => symbolFieldsMatch(security, normalized))
        if (match) {
            return match
        }
    }
    return undefined
}

export const resolveSecurityBySymbol = async (
    exchange: Exchange | string | number,
    symbol: string,
): Promise<Security | undefined> => {
    const trimmed = symbol.trim()
    if (!trimmed) {
        return undefined
    }

    const resolvedExchange = normalizeExchangeValue(exchange)
    const symbolVariants = getShareholderSearchSymbolVariants(trimmed)

    for (const variant of symbolVariants) {
        const cached = findCashSecurityBySymbol(variant)
        if (cached) {
            return cached
        }
    }

    if (resolvedExchange) {
        for (const variant of symbolVariants) {
            const security = await getSecurityAsync(getSecurityKey(resolvedExchange, variant))
            if (security) {
                return security
            }
        }
    }

    const exchangesToTry = resolvedExchange ? [resolvedExchange, ...SHAREHOLDER_LOOKUP_EXCHANGES] : SHAREHOLDER_LOOKUP_EXCHANGES
    const uniqueExchanges = [...new Set(exchangesToTry.filter(isValidExchange))]

    for (const variant of symbolVariants) {
        for (const lookupExchange of uniqueExchanges) {
            try {
                const lookupResults = await lookupSecuritiesBySymbols([variant], lookupExchange)
                const lookupMatch = matchLookupResultToSymbol(lookupResults, trimmed)
                if (lookupMatch) {
                    return lookupMatch
                }
            } catch {
                continue
            }
        }
    }

    return undefined
}

const decodeRouteSecurityKey = (rawKey: string): string => {
    const trimmed = rawKey.trim()
    if (!trimmed.includes('%')) {
        return trimmed
    }
    try {
        return decodeURIComponent(trimmed)
    } catch {
        return trimmed
    }
}

/** Resolve a security from a `/symbol/:symbol` route param (code, urlIdentifier, or encoded key). */
export const resolveSecurityFromRouteKey = async (rawKey: string): Promise<Security | undefined> => {
    const key = decodeRouteSecurityKey(rawKey)
    if (!key) {
        return undefined
    }

    if (SECURITY_KEY_REGEX.test(key)) {
        const security = await getSecurityAsync(key)
        if (security) {
            return security
        }
    }

    const colonIndex = key.indexOf(':')
    if (colonIndex > 0) {
        const exchangePart = key.slice(0, colonIndex)
        const identifier = key.slice(colonIndex + 1)
        if (identifier) {
            const resolved = await resolveSecurityBySymbol(exchangePart, identifier)
            if (resolved) {
                return resolved
            }
        }
    }

    return resolveSecurityBySymbol(Exchange.EXCHANGE_UNKNOWN, key)
}

export const calculateSnapshotDetails = (sec: Security, sn: Snapshot): {
    ltp: number,
    ltpChange: number,
    changePercentage: number,
    volume: number,
    referencedSecurity?: Security | undefined;
} | null => {
    if (!sn || !sec) {
        return null
    }
    const priceMultiplier = sec.priceMultiplier || 100
    const change = sn.lastTradedPrice / priceMultiplier - sn.previousClose / priceMultiplier
    const previousClose = sn.previousClose / priceMultiplier
    const volume = sn.volume
    const ltp = sn.lastTradedPrice / priceMultiplier
    const changePercentage = change * 100 / previousClose
    return {
        ltp: ltp,
        ltpChange: change,
        changePercentage: changePercentage,
        volume: volume,
        referencedSecurity: sn.referencedSecurity
    }
}

export const parseDetailsFromOptionSecurityCode = (securityCode: string, priceMultiplier: number = 100) => {
    if (!securityCode.length) return undefined
    const splits = securityCode.split('_').reverse()
    const expiryDate = parseDDMMMyyDate(splits[2])
    return {
        expiryName: expiryDate ? `${expiryDate.getDate()} ${shortMonthNames[expiryDate.getMonth()]}` : undefined,
        strike: Number(splits[1]) / priceMultiplier
    }

}

export const parseDetailsFromFutureSecurityCode = (securityCode: string) => {
    if (!securityCode.length) return undefined
    const splits = securityCode.split('_').reverse()
    const expiryDate = parseDDMMMyyDate(splits[0])
    return {
        expiryName: expiryDate ? `${expiryDate.getDate()} ${shortMonthNames[expiryDate.getMonth()]}` : undefined
    }
}

export const isFnOSecurity = (security: Security): void | Boolean => {
    return !!(security?.underlyingSecurityCode && security?.underlyingSecurityCode !== security?.code)
}

export const getSecuritiesFromSecuritiesObj = async (securities: Security[]): Promise<Security[] | undefined> => {
    const securitiesToFetch = securities.filter(v => !getSecurityFromCache(getSecurityKey(v.exchange, v.code)))
    if (securitiesToFetch.length) {
        try {
            const response = await fetchSecurities(securities.map((v) => ({
                exchange: v.exchange,
                code: v.code
            })))

            const secs = response
            response.forEach((v) => {
                window.securityManager.setSecurity(v)
            });
            return secs
        } catch (e) {
            console.error(e)
            return undefined
        }
    }
    return securities.map((v) => getSecurityFromCache(getSecurityKey(v.exchange, v.code)))
}

export const getPathForSecurity = (viewType: ViewType, security: Security): string => {
    switch (viewType) {
        case ViewType.OPTION_CHAIN:
            return generatePath(ROUTES.optionChain, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.STRATEGY_BUILDER:
            return generatePath(ROUTES.strategyBuilder, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.OI_OVERVIEW:
            return generatePath(ROUTES.oiOverview, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.OPEN_INTEREST:
            return generatePath(ROUTES.openInterest, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.OPEN_INTEREST_CHANGE:
            return generatePath(ROUTES.openInterestChange, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.MULTI_STRIKES_CHART:
            return generatePath(ROUTES.multiStrikeChart, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.SIMULATOR:
            return generatePath(ROUTES.simulator, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        default:
            return '#'
    }
}